import { useEffect, useMemo, useRef, useState } from 'react';
import Stomp, { Client, Subscription } from 'stompjs';
import SockJS from 'sockjs-client';
import { chatDebug, describeError, describeToken, pageState } from '../helpers/chatDebugLog';

const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 30000;
// Upper bound for one attempt (token fetch + SockJS open + STOMP CONNECTED). stompjs never times out
// waiting for CONNECTED, so without this a stalled attempt would block reconnects indefinitely.
const CONNECT_ATTEMPT_TIMEOUT_MS = 20000;
const DEFAULT_HEART_BEAT_MS = 25000;
// Set per environment via REACT_APP_STOMP_HEARTBEAT_MS; anything that isn't a positive number falls back.
const configuredHeartBeat = Number(window.__ENV__?.REACT_APP_STOMP_HEARTBEAT_MS);
const HEART_BEAT = configuredHeartBeat > 0 ? configuredHeartBeat : DEFAULT_HEART_BEAT_MS;

const EMPTY_STOMP_HEADERS: Record<string, string> = Object.freeze({});

// stompjs keeps `connected` true until it notices a dead socket itself, which can take minutes. As a
// backstop, treat a connection as dead if nothing at all (STOMP frame or SockJS heartbeat, which the
// server sends every 25s by default) has arrived for this long while the page is visible.
// 35s = one 25s heartbeat plus slack for a late one, so a healthy connection never hits it.
const SERVER_SILENCE_LIMIT_MS = 35000;
const SILENCE_CHECK_INTERVAL_MS = 5000;

// Some mobile networks hold back long-lived streamed responses, so on xhr-streaming the SockJS open frame
// and the STOMP CONNECTED reply arrive tens of seconds late or never, while sends still get through.
// SockJS would eventually fall back to xhr-polling on its own, but on a slow link its per-transport
// timeouts outlast our attempt limit, so every retry restarts on the transport that doesn't work. After
// repeated failures before CONNECTED, use polling (short, complete responses) for a while.
//
// It must not stick: a network switch also fails the first attempts, and a user who then lands on a good
// network should get WebSocket back. So it needs two failures in a row, expires, and is cleared whenever
// the network changes or the page becomes visible. A network that recovers silently fires none of those
// (and iOS Safari has no connection events at all), so while connected on polling we also probe WebSocket
// on a separate, throwaway socket; the live connection is never touched, so a probe is never a chat blip.
const POLLING_ONLY_STORAGE_KEY = 'p2p_sockjs_polling_only';
const POLLING_TRANSPORTS = ['xhr-polling'];
const FAILURES_BEFORE_POLLING = 2;
const POLLING_ONLY_TTL_MS = 10 * 60 * 1000;
let pollingOnlyUntilFallback = 0; // used when sessionStorage is unavailable
const WEBSOCKET_PROBE_INTERVAL_MS = 90000;
const WEBSOCKET_PROBE_TIMEOUT_MS = 15000;

/** Stored value is the expiry time in ms. */
function isPollingOnly(): boolean {
	try {
		return Number(sessionStorage.getItem(POLLING_ONLY_STORAGE_KEY)) > Date.now();
	} catch {
		return pollingOnlyUntilFallback > Date.now();
	}
}

function setPollingOnly() {
	pollingOnlyUntilFallback = Date.now() + POLLING_ONLY_TTL_MS;
	try {
		sessionStorage.setItem(POLLING_ONLY_STORAGE_KEY, String(pollingOnlyUntilFallback));
	} catch {
		// in-memory value above still applies for this page
	}
}

function clearPollingOnly() {
	pollingOnlyUntilFallback = 0;
	try {
		sessionStorage.removeItem(POLLING_ONLY_STORAGE_KEY);
	} catch {
		// nothing stored
	}
}

interface UseResilientStompClientProps {
	url: string;
	/**
	 * Fetches a short-lived websocket JWT. Called before every connect attempt so a reconnect after a
	 * long suspend (mobile background tab, bfcache) never retries with an expired token.
	 */
	getSocketJwtToken: () => Promise<string>;
	/** No connection while false. */
	enabled?: boolean;
	/** Extra STOMP connect headers (e.g. orderNumber). Authorization is set from the fetched token. */
	headers?: Record<string, string>;
	onConnect?: (client: Client) => void;
	onDisconnect?: () => void;
	/** Name used in the `[chat-debug]` logs, e.g. 'order-chat'. Defaults to the url. */
	label?: string;
}

/**
 * STOMP over SockJS that keeps reconnecting until unmounted: capped exponential backoff with no retry
 * limit, plus an immediate retry when the page becomes visible, regains focus, comes back online, or
 * is restored from bfcache. `connected` / `stompClient` drop as soon as the socket closes, the device
 * goes offline, the server goes silent, or a caller reports the connection dead via `reportDead`.
 */
export function useResilientStompClient({
	url,
	getSocketJwtToken,
	enabled = true,
	headers,
	onConnect,
	onDisconnect,
	label,
}: UseResilientStompClientProps) {
	const clientRef = useRef<Client | null>(null);
	const socketRef = useRef<{ close: () => void } | null>(null);
	const [connected, setConnected] = useState(false);
	const [socketJwtToken, setSocketJwtToken] = useState<string | null>(null);
	const retryCount = useRef(0);
	const retryTimeout = useRef<NodeJS.Timeout | null>(null);
	const attemptTimeout = useRef<NodeJS.Timeout | null>(null);
	const activeSubscriptions = useRef<Map<string, Subscription>>(new Map());
	const connecting = useRef(false);
	// Bumped on every teardown so callbacks from an abandoned socket or token request are ignored.
	const generation = useRef(0);
	// Diagnostics only.
	const attemptNumber = useRef(0);
	const connectedAt = useRef<number | null>(null);
	const hiddenAt = useRef<number | null>(null);
	const lastServerActivity = useRef(0);

	const urlRef = useRef(url);
	const enabledRef = useRef(enabled);
	const extraHeadersRef = useRef<Record<string, string>>(EMPTY_STOMP_HEADERS);
	const getSocketJwtTokenRef = useRef(getSocketJwtToken);
	const onConnectRef = useRef(onConnect);
	const onDisconnectRef = useRef(onDisconnect);

	urlRef.current = url;
	enabledRef.current = enabled;
	extraHeadersRef.current = headers ?? EMPTY_STOMP_HEADERS;
	getSocketJwtTokenRef.current = getSocketJwtToken;
	onConnectRef.current = onConnect;
	onDisconnectRef.current = onDisconnect;
	const scopeRef = useRef(label ?? url);
	scopeRef.current = label ?? url;
	const log = {
		info: (event: string, data?: Record<string, unknown>) => chatDebug.info(scopeRef.current, event, data),
		warn: (event: string, data?: Record<string, unknown>) => chatDebug.warn(scopeRef.current, event, data),
		error: (event: string, data?: Record<string, unknown>) => chatDebug.error(scopeRef.current, event, data),
	};
	const connectionLifetimeSec = () =>
		connectedAt.current === null ? null : Math.round((Date.now() - connectedAt.current) / 1000);

	const extraHeadersKey = useMemo(() => JSON.stringify(headers ?? EMPTY_STOMP_HEADERS), [headers]);

	const clearSubscriptions = () => {
		activeSubscriptions.current.forEach((sub) => {
			try {
				sub.unsubscribe();
			} catch {
				// the socket is already gone
			}
		});
		activeSubscriptions.current.clear();
	};

	const clearRetry = () => {
		if (retryTimeout.current) clearTimeout(retryTimeout.current);
		retryTimeout.current = null;
	};

	const clearAttemptTimeout = () => {
		if (attemptTimeout.current) clearTimeout(attemptTimeout.current);
		attemptTimeout.current = null;
	};

	const connectRef = useRef<() => void>(() => {});

	// Consecutive attempts that failed before CONNECTED; reset by a successful connect or a network change.
	const failuresBeforeConnect = useRef(0);
	// SockJS transport of the live connection ('websocket', 'xhr-streaming', 'xhr-polling', ...).
	const transportRef = useRef<string | null>(null);

	const preferPolling = (reason: string) => {
		if (isPollingOnly()) return;
		failuresBeforeConnect.current += 1;
		if (failuresBeforeConnect.current < FAILURES_BEFORE_POLLING) {
			log.info('attempt failed before CONNECTED', { reason, failures: failuresBeforeConnect.current });
			return;
		}
		setPollingOnly();
		log.warn('switching to xhr-polling for a while', {
			reason,
			failures: failuresBeforeConnect.current,
			forMin: POLLING_ONLY_TTL_MS / 60000,
		});
	};

	const onNetworkChanged = (reason: string) => {
		failuresBeforeConnect.current = 0;
		if (isPollingOnly()) {
			clearPollingOnly();
			log.info('network changed, trying WebSocket again', { reason });
		}
	};

	const probeRef = useRef<{ close: () => void } | null>(null);

	const stopProbe = () => {
		probeRef.current?.close();
		probeRef.current = null;
	};

	/**
	 * Opens a throwaway SockJS with the default transports while the live connection stays on polling. The
	 * SockJS open frame arriving over the 'websocket' transport proves WebSocket works again, so the flag is
	 * cleared and the next reconnect uses it. No STOMP CONNECT: that would need a token and would show up
	 * server side as a second session for this user (and its disconnect as them leaving).
	 */
	const probeWebSocket = () => {
		if (probeRef.current || !clientRef.current?.connected || !isPollingOnly()) return;
		const startedAt = Date.now();
		let done = false;
		const socket = new SockJS(urlRef.current);
		const finish = (ok: boolean, detail: Record<string, unknown>) => {
			if (done) return;
			done = true;
			clearTimeout(timeout);
			probeRef.current = null;
			try {
				socket.close();
			} catch {
				// already closed
			}
			if (ok) {
				failuresBeforeConnect.current = 0;
				clearPollingOnly();
				log.info('WebSocket probe succeeded, next reconnect uses WebSocket', { tookMs: Date.now() - startedAt });
			} else {
				log.info('WebSocket probe failed, staying on xhr-polling', { ...detail, tookMs: Date.now() - startedAt });
			}
		};
		const timeout = setTimeout(() => finish(false, { reason: 'timed out' }), WEBSOCKET_PROBE_TIMEOUT_MS);
		probeRef.current = { close: () => finish(false, { reason: 'stopped' }) };
		socket.addEventListener('open', () => {
			const transport = (socket as any).transport;
			finish(transport === 'websocket', { reason: 'opened on a fallback transport', transport });
		});
		socket.addEventListener('close', (event: any) => finish(false, { reason: 'closed', code: event?.code }));
		log.info('WebSocket probe started');
	};

	const scheduleRetry = () => {
		if (!enabledRef.current) return;
		clearRetry();
		const backoff = Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount.current), RETRY_MAX_DELAY_MS);
		const delay = backoff * (0.8 + Math.random() * 0.4);
		retryCount.current += 1;
		log.info('retry scheduled', { retry: retryCount.current, delayMs: Math.round(delay), ...pageState() });
		retryTimeout.current = setTimeout(() => connectRef.current(), delay);
	};

	connectRef.current = async () => {
		if (!enabledRef.current || connecting.current || clientRef.current?.connected) {
			log.info('connect skipped', {
				enabled: enabledRef.current,
				connecting: connecting.current,
				connected: !!clientRef.current?.connected,
			});
			return;
		}
		connecting.current = true;
		clearRetry();
		const attempt = generation.current;
		const attemptNo = ++attemptNumber.current;
		const startedAt = Date.now();
		const elapsedMs = () => Date.now() - startedAt;
		log.info('connect attempt started', { attempt: attemptNo, retry: retryCount.current, ...pageState() });
		attemptTimeout.current = setTimeout(() => {
			if (attempt !== generation.current) return;
			log.warn('connect attempt timed out, abandoning it', {
				attempt: attemptNo,
				timeoutMs: CONNECT_ATTEMPT_TIMEOUT_MS,
				stage: socketRef.current ? 'waiting for STOMP CONNECTED' : 'waiting for socket token',
				...pageState(),
			});
			preferPolling('connect attempt timed out');
			// Invalidate the stalled attempt so its late callbacks are ignored, then back off and retry.
			generation.current += 1;
			attemptTimeout.current = null;
			connecting.current = false;
			const socket = socketRef.current;
			socketRef.current = null;
			try {
				socket?.close();
			} catch {
				// already closed
			}
			scheduleRetry();
		}, CONNECT_ATTEMPT_TIMEOUT_MS);

		let token: string;
		try {
			token = await getSocketJwtTokenRef.current();
		} catch (err) {
			if (attempt !== generation.current) {
				log.info('ignoring token failure from an abandoned attempt', { attempt: attemptNo });
				return;
			}
			clearAttemptTimeout();
			connecting.current = false;
			log.error('socket token fetch failed', { attempt: attemptNo, tookMs: elapsedMs(), ...describeError(err) });
			scheduleRetry();
			return;
		}
		if (attempt !== generation.current) {
			log.info('ignoring token from an abandoned attempt', { attempt: attemptNo });
			return;
		}
		log.info('socket token fetched', { attempt: attemptNo, tookMs: elapsedMs(), ...describeToken(token) });
		setSocketJwtToken(token);

		const pollingOnly = isPollingOnly();
		const socket = new SockJS(urlRef.current, null, pollingOnly ? { transports: POLLING_TRANSPORTS } : undefined);
		socketRef.current = socket;
		log.info('sockjs created', { attempt: attemptNo, transports: pollingOnly ? POLLING_TRANSPORTS : 'default' });
		// Listeners sit alongside the handlers stompjs assigns. `transport` shows whether the WebSocket
		// upgrade worked ('websocket') or SockJS fell back (e.g. 'xhr-streaming').
		socket.addEventListener('open', () => {
			log.info('sockjs open', { attempt: attemptNo, transport: (socket as any).transport, tookMs: elapsedMs() });
		});
		const markServerActivity = () => {
			if (attempt === generation.current) lastServerActivity.current = Date.now();
		};
		socket.addEventListener('message', markServerActivity);
		socket.addEventListener('heartbeat', markServerActivity);
		socket.addEventListener('close', (event: any) => {
			log.warn('sockjs closed', {
				attempt: attemptNo,
				code: event?.code,
				reason: event?.reason,
				wasClean: event?.wasClean,
				...pageState(),
			});
		});
		const stompClient = Stomp.over(socket);
		stompClient.debug = () => {};
		stompClient.heartbeat.outgoing = HEART_BEAT;
		stompClient.heartbeat.incoming = HEART_BEAT;

		const mergedHeaders = { ...extraHeadersRef.current, Authorization: `Bearer ${token}` };
		// stompjs can report one failure twice (ERROR frame, then socket close).
		let failed = false;

		stompClient.connect(
			mergedHeaders,
			() => {
				if (attempt !== generation.current) {
					log.info('ignoring CONNECTED from an abandoned attempt', { attempt: attemptNo });
					return;
				}
				clearAttemptTimeout();
				log.info('STOMP connected', {
					attempt: attemptNo,
					afterRetries: retryCount.current,
					tookMs: elapsedMs(),
					transport: (socket as any).transport,
					heartbeatMs: HEART_BEAT,
				});
				connecting.current = false;
				retryCount.current = 0;
				failuresBeforeConnect.current = 0;
				transportRef.current = (socket as any).transport ?? null;
				connectedAt.current = Date.now();
				lastServerActivity.current = Date.now();
				clientRef.current = stompClient;
				setConnected(true);
				clearSubscriptions();

				const cb = onConnectRef.current;
				if (cb) {
					const safeSubscribe = (destination: string, callback: (msg: any) => void, force = false) => {
						if (!force && activeSubscriptions.current.has(destination)) {
							return;
						}
						const sub = stompClient.subscribe(destination, callback);
						activeSubscriptions.current.set(destination, sub);
					};

					cb({ ...stompClient, subscribe: safeSubscribe } as Client);
				}
			},
			(error) => {
				if (attempt !== generation.current || failed) return;
				failed = true;
				clearAttemptTimeout();
				log.error('STOMP connection lost or failed', {
					attempt: attemptNo,
					wasConnected: connectedAt.current !== null,
					connectionLifetimeSec: connectionLifetimeSec(),
					...describeError(error),
					...pageState(),
				});
				if (connectedAt.current === null) preferPolling('connection failed before STOMP CONNECTED');
				connectedAt.current = null;
				connecting.current = false;
				clientRef.current = null;
				socketRef.current = null;
				setConnected(false);
				clearSubscriptions();
				try {
					socket.close();
				} catch {
					// already closed
				}
				onDisconnectRef.current?.();
				scheduleRetry();
			},
		);
	};

	const teardown = (reason: string) => {
		stopProbe();
		log.info('teardown', {
			reason,
			wasConnected: !!clientRef.current?.connected,
			connectionLifetimeSec: connectionLifetimeSec(),
		});
		connectedAt.current = null;
		generation.current += 1;
		clearRetry();
		clearAttemptTimeout();
		connecting.current = false;
		clearSubscriptions();
		const client = clientRef.current;
		const socket = socketRef.current;
		clientRef.current = null;
		socketRef.current = null;
		try {
			if (client?.connected) client.disconnect(() => {});
			else socket?.close();
		} catch {
			// already closed
		}
		setConnected(false);
	};

	/**
	 * Something proved the connection dead while stompjs still reports it connected (a send that never
	 * echoed back, server silence, the device going offline). Drop it so `connected` flips immediately,
	 * then reconnect.
	 */
	const reportDead = (reason: string) => {
		if (!enabledRef.current || !clientRef.current?.connected) return;
		log.warn('connection considered dead, reconnecting', {
			reason,
			connectionLifetimeSec: connectionLifetimeSec(),
			...pageState(),
		});
		teardown(reason);
		onDisconnectRef.current?.();
		retryCount.current = 0;
		// Offline: back off; the 'online' event reconnects immediately.
		if (navigator.onLine) connectRef.current();
		else scheduleRetry();
	};

	/** Skip the backoff wait and try now. */
	const reconnectNow = (reason: string) => {
		if (!enabledRef.current || clientRef.current?.connected) {
			log.info('reconnect-now skipped', {
				reason,
				enabled: enabledRef.current,
				connected: !!clientRef.current?.connected,
			});
			return;
		}
		retryCount.current = 0;
		if (connecting.current) {
			log.info('reconnect-now skipped, attempt already running', { reason });
			return;
		}
		log.info('reconnect-now', { reason, ...pageState() });
		connectRef.current();
	};

	useEffect(() => {
		if (!enabled) {
			teardown('disabled');
			setSocketJwtToken(null);
			return;
		}
		log.info('starting', { url, heartbeatMs: HEART_BEAT, ...pageState() });
		retryCount.current = 0;
		connectRef.current();
		return () => {
			teardown('unmount or url/headers changed');
		};
	}, [url, enabled, extraHeadersKey]);

	useEffect(() => {
		const handleVisibility = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAt.current = Date.now();
				log.info('page hidden', { connected: !!clientRef.current?.connected });
				return;
			}
			const hiddenForSec = hiddenAt.current === null ? null : Math.round((Date.now() - hiddenAt.current) / 1000);
			hiddenAt.current = null;
			// Timers and events were suspended while hidden, so the silence backstop restarts from here.
			lastServerActivity.current = Date.now();
			log.info('page visible', { hiddenForSec, connected: !!clientRef.current?.connected });
			// The network may well have recovered while hidden; the next (re)connect tries WebSocket again.
			onNetworkChanged('page visible');
			reconnectNow('page visible');
		};
		const handleOnline = () => {
			onNetworkChanged('network online');
			reconnectNow('network online');
		};
		// Wi-Fi <-> mobile data switches often fire no online/offline events; Network Information API
		// (Chrome/Android; absent on iOS Safari) reports them. It also fires on quality changes, so this only
		// resets the transport choice and never drops a working connection.
		const connection = (navigator as any).connection;
		const handleConnectionChange = () => {
			onNetworkChanged('network type changed');
			reconnectNow('network type changed');
		};
		const handleFocus = () => reconnectNow('window focus');
		const handleOffline = () => {
			log.warn('network offline', { connected: !!clientRef.current?.connected });
			reportDead('network offline');
		};
		// iOS Safari can restore a page from bfcache without firing visibilitychange; the old socket
		// is dead even if stompjs still reports it as connected.
		const handlePageShow = (event: PageTransitionEvent) => {
			if (!event.persisted || !enabledRef.current) return;
			log.info('page restored from bfcache, rebuilding connection');
			teardown('bfcache restore');
			retryCount.current = 0;
			connectRef.current();
		};

		document.addEventListener('visibilitychange', handleVisibility);
		window.addEventListener('online', handleOnline);
		connection?.addEventListener?.('change', handleConnectionChange);
		window.addEventListener('offline', handleOffline);
		// Some mobile browsers regain focus on resume without a visibility transition.
		window.addEventListener('focus', handleFocus);
		window.addEventListener('pageshow', handlePageShow);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibility);
			window.removeEventListener('online', handleOnline);
			connection?.removeEventListener?.('change', handleConnectionChange);
			window.removeEventListener('offline', handleOffline);
			window.removeEventListener('focus', handleFocus);
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			if (!clientRef.current?.connected || document.visibilityState !== 'visible') return;
			const silentMs = Date.now() - lastServerActivity.current;
			if (silentMs > SERVER_SILENCE_LIMIT_MS) reportDead(`no server traffic for ${Math.round(silentMs / 1000)}s`);
		}, SILENCE_CHECK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			if (document.visibilityState === 'visible' && navigator.onLine) probeWebSocket();
		}, WEBSOCKET_PROBE_INTERVAL_MS);
		return () => {
			clearInterval(interval);
			stopProbe();
		};
	}, []);

	return {
		stompClient: clientRef.current,
		connected,
		/** Transport of the live connection, null while disconnected. */
		transport: connected ? transportRef.current : null,
		socketJwtToken,
		reconnect: () => reconnectNow('manual'),
		reportDead,
	};
}
